/**
 * Editable, multilingual (AR / EN / FR), markdown-aware static page.
 *
 * Editing is ADMIN ONLY (gated by `useIsAdmin`). Visitors only see the
 * rendered content — no Edit / History buttons appear for them. Saves are:
 *   1. Mirrored to localStorage for instant local preview + revision log.
 *   2. Enqueued into the same GitHub publish pipeline used by the dashboard
 *      (`uploadTextToGithub` -> pending queue -> batch commit), so once
 *      published the change goes LIVE for every visitor.
 *   3. On mount, the latest published JSON is fetched from GitHub raw
 *      (when GitHub is configured) so all visitors see the live version.
 */
import { useState, useEffect, useMemo } from "react";
import { useTheme, type LanguageCode } from "@/lib/theme-context";
import { uploadTextToGithub, getGitHubConfig } from "@/lib/github-publisher";
import { useIsAdmin } from "@/lib/admin-acl";
import { Edit3, History, RotateCcw, X, Check, Eye, Pencil } from "lucide-react";
import { Markdown } from "./Markdown";
import { MarkdownEditor } from "./MarkdownEditor";

type LangMap = { ar: string; en: string; fr: string };

interface Revision {
  ts: number;
  content: LangMap;
  by: string;
}

export interface EditableStaticPageProps {
  pageKey: string;
  titleAr: string;
  titleEn: string;
  titleFr?: string;
  defaultParagraphsAr: string[];
  defaultParagraphsEn: string[];
  defaultParagraphsFr?: string[];
}

const LANGS: { code: LanguageCode; label: string }[] = [
  { code: "ar", label: "العربية" },
  { code: "en", label: "English" },
  { code: "fr", label: "Français" },
];

const tt = (lang: LanguageCode, ar: string, en: string, fr: string) =>
  lang === "ar" ? ar : lang === "fr" ? fr : en;

function joinDefaults(paragraphs?: string[], fallback?: string[]) {
  const p = paragraphs && paragraphs.length ? paragraphs : (fallback ?? []);
  return p.join("\n\n");
}

export function EditableStaticPage(props: EditableStaticPageProps) {
  const {
    pageKey,
    titleAr,
    titleEn,
    titleFr,
    defaultParagraphsAr,
    defaultParagraphsEn,
    defaultParagraphsFr,
  } = props;

  const { language } = useTheme();
  // Editing + history are ADMIN ONLY. Visitors never see these controls.
  const isAdmin = useIsAdmin();

  const storageKey = `rawan-page-v10-${pageKey}`;
  const historyKey = `rawan-page-history-v10-${pageKey}`;

  const defaults: LangMap = useMemo(
    () => ({
      ar: joinDefaults(defaultParagraphsAr),
      en: joinDefaults(defaultParagraphsEn),
      fr: joinDefaults(defaultParagraphsFr, defaultParagraphsEn),
    }),
    [defaultParagraphsAr, defaultParagraphsEn, defaultParagraphsFr],
  );

  const [content, setContent] = useState<LangMap>(defaults);
  const [draft, setDraft] = useState<LangMap>(defaults);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<LanguageCode>(language);
  const [editorMode, setEditorMode] = useState<"edit" | "preview">("edit");
  const [showConfirm, setShowConfirm] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<Revision[]>([]);

  // Load saved content + history.
  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved) as Partial<LangMap>;
        const merged: LangMap = {
          ar: parsed.ar ?? defaults.ar,
          en: parsed.en ?? defaults.en,
          fr: parsed.fr ?? defaults.fr,
        };
        setContent(merged);
        setDraft(merged);
      }
      const h = localStorage.getItem(historyKey);
      if (h) setHistory(JSON.parse(h) as Revision[]);
    } catch { /* ignore */ }
  }, [storageKey, historyKey, defaults]);

  // Keep active tab in sync with site language when the user has not started editing.
  useEffect(() => {
    if (!isEditing) setActiveTab(language);
  }, [language, isEditing]);

  const title = tt(language, titleAr, titleEn, titleFr || titleEn);
  const currentContent = content[language] || content.en || content.ar;

  const openEditor = () => {
    setDraft(content);
    setActiveTab(language);
    setEditorMode("edit");
    setIsEditing(true);
  };

  const cancelEditor = () => {
    setDraft(content);
    setIsEditing(false);
    setShowConfirm(false);
  };

  const remotePath = `pages/${pageKey}.json`;

  const persist = (next: LangMap, by: string) => {
    setContent(next);
    try {
      localStorage.setItem(storageKey, JSON.stringify({ ...next, ts: Date.now() }));
      const newHistory: Revision[] = [{ ts: Date.now(), content: next, by }, ...history].slice(0, 10);
      setHistory(newHistory);
      localStorage.setItem(historyKey, JSON.stringify(newHistory));
    } catch { /* ignore */ }
    // Mirror to the GitHub publish pipeline (same one the dashboard uses).
    // Failure is silent — the local copy is already saved and the dashboard
    // "المسودّات" tab will surface any retries.
    void uploadTextToGithub({
      absolutePath: `content/${remotePath}`,
      textContent: JSON.stringify({ ...next, ts: Date.now(), by }, null, 2),
      message: `pages: update ${pageKey} (${by})`,
    }).catch(() => { /* ignore */ });
  };

  // Pull the published JSON from GitHub raw so every visitor sees the live
  // version, not just whoever edited it in their own browser.
  useEffect(() => {
    const cfg = getGitHubConfig();
    if (!cfg) return;
    const url =
      `https://raw.githubusercontent.com/${cfg.repo}/${cfg.branch}/` +
      `${cfg.basePath}/${remotePath}`;
    let cancelled = false;
    fetch(url, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        const merged: LangMap = {
          ar: typeof data.ar === "string" ? data.ar : defaults.ar,
          en: typeof data.en === "string" ? data.en : defaults.en,
          fr: typeof data.fr === "string" ? data.fr : defaults.fr,
        };
        setContent(merged);
        setDraft(merged);
      })
      .catch(() => { /* offline / 404 — keep local copy */ });
    return () => { cancelled = true; };
  }, [remotePath, defaults]);

  const confirmSave = () => {
    persist(draft, "visitor");
    setIsEditing(false);
    setShowConfirm(false);
  };

  const resetDefaults = () => {
    setDraft(defaults);
    persist(defaults, "visitor:reset");
    setIsEditing(false);
    setShowConfirm(false);
  };

  const revertTo = (rev: Revision) => {
    persist(rev.content, "visitor:revert");
    setDraft(rev.content);
    setShowHistory(false);
  };

  const tabDir = (code: LanguageCode): "rtl" | "ltr" => (code === "ar" ? "rtl" : "ltr");

  return (
    <div className="relative">
      <div className="flex items-center justify-between gap-3 mb-6">
        <h1 className="text-3xl sm:text-4xl font-extrabold">{title}</h1>
        {isAdmin && !isEditing && (
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setShowHistory(true)}
              title={tt(language, "السجل", "History", "Historique")}
              aria-label="history"
              className="p-2 rounded-lg bg-muted hover:bg-accent text-muted-foreground hover:text-foreground transition"
            >
              <History className="h-4 w-4" />
            </button>
            <button
              onClick={openEditor}
              title={tt(language, "تعديل", "Edit", "Modifier")}
              aria-label="edit"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary/10 hover:bg-primary text-primary hover:text-primary-foreground text-xs font-bold transition shadow-sm"
            >
              <Pencil className="h-3.5 w-3.5" />
              <span>{tt(language, "تعديل", "Edit", "Modifier")}</span>
            </button>
          </div>
        )}
      </div>

      {isEditing && isAdmin ? (
        <div className="bg-card border border-border rounded-2xl p-4 sm:p-5 shadow-lg mb-8 space-y-4">
          {/* Language tabs */}
          <div className="flex items-center justify-between gap-2 border-b border-border/60 pb-3 flex-wrap">
            <div className="flex gap-1 bg-muted rounded-lg p-1">
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  onClick={() => setActiveTab(l.code)}
                  className={`px-3 py-1 rounded-md text-xs font-bold transition ${
                    activeTab === l.code
                      ? "bg-primary text-primary-foreground shadow"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setEditorMode("edit")}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-bold transition ${
                  editorMode === "edit" ? "bg-accent text-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Edit3 className="h-3 w-3" /> {tt(language, "تحرير", "Edit", "Éditer")}
              </button>
              <button
                onClick={() => setEditorMode("preview")}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-bold transition ${
                  editorMode === "preview" ? "bg-accent text-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Eye className="h-3 w-3" /> {tt(language, "معاينة", "Preview", "Aperçu")}
              </button>
            </div>
          </div>

          {editorMode === "edit" ? (
            <MarkdownEditor
              key={activeTab}
              dir={tabDir(activeTab)}
              value={draft[activeTab]}
              onChange={(v) => setDraft((d) => ({ ...d, [activeTab]: v }))}
              label={tt(activeTab, "النص العربي — يدعم ماركداون", "English text — Markdown supported", "Texte français — Markdown supporté")}
              placeholder={tt(activeTab,
                "اكتب باستخدام ماركداون: **عريض**، *مائل*، # عنوان، - قائمة …",
                "Write in Markdown: **bold**, *italic*, # heading, - list …",
                "Écrivez en Markdown : **gras**, *italique*, # titre, - liste …",
              )}
              minRows={10}
            />
          ) : (
            <div
              dir={tabDir(activeTab)}
              className="border border-border rounded-xl p-4 bg-background min-h-[200px]"
            >
              <Markdown source={draft[activeTab] || ""} />
            </div>
          )}

          <div className="flex flex-wrap justify-end items-center gap-2 pt-2 border-t border-border/60">
            <button
              onClick={resetDefaults}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs text-destructive hover:bg-destructive/10 font-semibold transition"
              title={tt(language, "استعادة الافتراضي", "Reset to defaults", "Réinitialiser")}
            >
              <RotateCcw className="h-3 w-3" />
              <span>{tt(language, "الافتراضي", "Defaults", "Défauts")}</span>
            </button>
            <button
              onClick={cancelEditor}
              className="px-4 py-2 rounded-lg border border-border text-xs font-bold hover:bg-accent transition"
            >
              {tt(language, "إلغاء", "Cancel", "Annuler")}
            </button>
            <button
              onClick={() => setShowConfirm(true)}
              className="btn-primary px-4 py-2 text-xs inline-flex items-center gap-1.5 font-bold"
            >
              <Check className="h-3.5 w-3.5" />
              <span>{tt(language, "حفظ التعديلات", "Save changes", "Enregistrer")}</span>
            </button>
          </div>
        </div>
      ) : null}

      <div className="text-sm sm:text-base text-foreground/90">
        <Markdown source={currentContent} />
      </div>

      {/* Confirm + preview modal */}
      {showConfirm && (
        <ModalShell onClose={() => setShowConfirm(false)} title={tt(language, "تأكيد الحفظ ومعاينة", "Confirm & preview", "Confirmer & aperçu")}>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pe-1">
            {LANGS.map((l) => (
              <div key={l.code} className="border border-border rounded-xl">
                <div className="px-3 py-2 text-xs font-bold border-b border-border/60 bg-muted/40 flex items-center justify-between">
                  <span>{l.label}</span>
                  {draft[l.code] !== content[l.code] && (
                    <span className="text-[10px] uppercase font-extrabold text-primary">
                      {tt(language, "تم التعديل", "Modified", "Modifié")}
                    </span>
                  )}
                </div>
                <div dir={tabDir(l.code)} className="p-3 text-sm">
                  <Markdown source={draft[l.code] || ""} />
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-4 mt-2 border-t border-border">
            <button onClick={() => setShowConfirm(false)} className="px-4 py-2 rounded-lg border border-border text-xs font-bold hover:bg-accent">
              {tt(language, "رجوع", "Back", "Retour")}
            </button>
            <button onClick={confirmSave} className="btn-primary px-4 py-2 text-xs inline-flex items-center gap-1.5 font-bold">
              <Check className="h-3.5 w-3.5" />
              <span>{tt(language, "تأكيد وحفظ", "Confirm & save", "Confirmer")}</span>
            </button>
          </div>
        </ModalShell>
      )}

      {/* History modal */}
      {showHistory && (
        <ModalShell onClose={() => setShowHistory(false)} title={tt(language, "سجل التعديلات", "Revision history", "Historique des révisions")}>
          {history.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              {tt(language, "لا توجد تعديلات سابقة بعد.", "No revisions yet.", "Aucune révision pour le moment.")}
            </p>
          ) : (
            <ul className="space-y-2 max-h-[55vh] overflow-y-auto pe-1">
              {history.map((rev) => (
                <li key={rev.ts} className="border border-border rounded-xl p-3 flex items-center justify-between gap-3">
                  <div className="text-xs">
                    <div className="font-bold text-foreground">
                      {new Date(rev.ts).toLocaleString(language === "ar" ? "ar-MA" : language === "fr" ? "fr-FR" : "en-US")}
                    </div>
                    <div className="text-muted-foreground">{rev.by}</div>
                  </div>
                  <button
                    onClick={() => revertTo(rev)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary/10 hover:bg-primary text-primary hover:text-primary-foreground text-xs font-bold transition"
                  >
                    <RotateCcw className="h-3 w-3" />
                    {tt(language, "استعادة", "Restore", "Restaurer")}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </ModalShell>
      )}
    </div>
  );
}

function ModalShell({
  title, onClose, children,
}: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-2xl p-5 sm:p-6 animate-in fade-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-extrabold">{title}</h3>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-accent text-muted-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
